/**
 * Which network calls may have their request/response BODIES stored on an issue report.
 *
 * ONE list, used by the browser capture (client/src/lib/contextCapture) AND the server (functions
 * issueTriage/submitIssueReport), so the two can never drift. Bodies are kept ONLY for OUR OWN hosts and dropped for
 * everything else: the next token service the app calls (Google, Intuit, Dialpad, App Check ...) is then never stored
 * by default, instead of being stored until someone remembers to add it to a block list (#7643: ISS-602 saved a
 * Firebase refresh token from securetoken.googleapis.com, and a block list of three hosts had already drifted).
 *
 * Method, URL and status of every call are still stored; only the bodies are subject to this list.
 *
 * THE URL IS PARSED BY THE CALLER'S OWN WHATWG PARSER, not by code in this package. A hand-written parser disagrees
 * with the browser on inputs like "\u0001https://securetoken.googleapis.com/..." or "///securetoken.googleapis.com/..."
 * (the browser goes to the real host; the hand-written one called them relative, i.e. ours). This package has no DOM or
 * node typings, so the parser is INJECTED: both callers pass `(u, base) => new URL(u, base)`. Anything the parser
 * cannot parse, or that has a control character or surrounding whitespace, is NOT ours (fail closed).
 */

/** The parts of a parsed URL this check reads (a WHATWG `URL` satisfies it). */
export interface ParsedUrlParts {
  readonly protocol: string;
  readonly hostname: string;
}

/** Parses `url` against `base` exactly as the platform does, and throws when it cannot. */
export type UrlParser = (url: string, base: string) => ParsedUrlParts;

/** A relative URL (or one starting with `//`) is resolved against this origin, as the app's own origin. */
const OWN_ORIGIN = "https://app.onpointgen.com";

/** Hosts that are exactly ours (the app, its other sites, the dev machine). */
const OWN_HOSTS: ReadonlySet<string> = new Set([
  "onpointgen.com",
  "quoter-saas.web.app",
  "quoter-saas.firebaseapp.com",
  "tech-onpointgen.web.app",
  "sales-onpointgen.web.app",
  "onpointtime.web.app",
  "shop-onpointgen.web.app",
  // The ONE Cloud Run service the app calls (the IR router): the connect-src of firebase.json names this host and no
  // other *.run.app. Pinned, not a wildcard: a Cloud Run host does not carry the project, so `*.run.app` would be
  // every tenant's service.
  "ir-router-705752337023.us-central1.run.app",
  "localhost",
  "127.0.0.1",
]);

/** This project's Cloud Functions hosts: `<region>-quoter-saas.cloudfunctions.net`, not any other project's. */
const OWN_FUNCTIONS_HOST = /^[a-z]+-[a-z]+[0-9]+-quoter-saas\.cloudfunctions\.net$/;

/** Subdomains of onpointgen.com are ours. */
const OWN_DOMAIN_SUFFIX = ".onpointgen.com";

/** Control characters and the space, anywhere (the WHATWG parser silently strips them, so they hide the real host). */
// eslint-disable-next-line no-control-regex
const HAS_CONTROL_OR_SPACE = /[\u0000- \u007f]/;

/** `https:evil.example` and `http:/x`: the parser reads these as a path on the base, a form no honest caller sends. */
const SCHEME_WITHOUT_AUTHORITY = /^https?:(?![/\\]{2})/i;

/**
 * @param {unknown} url the captured request URL (absolute, or relative to the app)
 * @param {UrlParser} parseUrl the platform's WHATWG parser, e.g. `(u, base) => new URL(u, base)`
 * @return {boolean} true only when the URL is http(s) on one of our own hosts as the PLATFORM resolves it; false for
 *   anything else, including a URL that cannot be parsed or that contains a control character or whitespace
 */
export function isOwnBodyCaptureUrl(url: unknown, parseUrl: UrlParser): boolean {
  if (typeof url !== "string" || url === "" || HAS_CONTROL_OR_SPACE.test(url) || SCHEME_WITHOUT_AUTHORITY.test(url)) return false;
  let parsed: ParsedUrlParts;
  try {
    parsed = parseUrl(url, OWN_ORIGIN);
  } catch {
    return false;
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return false;
  // `host.` (trailing dot) is the same host; the parsed hostname already drops userinfo and the port.
  const host = parsed.hostname.toLowerCase().replace(/\.$/, "");
  return OWN_HOSTS.has(host) || host.endsWith(OWN_DOMAIN_SUFFIX) || OWN_FUNCTIONS_HOST.test(host);
}
